import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { Star, ShieldCheck, Heart } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";
import { ActivityBookingDialog } from "@/components/ActivityBookingDialog";
import { BASE_ISLANDS, V3_ISLANDS } from "@/lib/islands";

const ALL_ISLANDS = [...BASE_ISLANDS, ...V3_ISLANDS];
const CATS = ["activite", "restaurant", "lieu", "decouverte"];
const CAT_LABEL = { activite: "tActivity", restaurant: "tRestaurant", lieu: "tPlace", decouverte: "tDiscovery" };

export default function Activities() {
  const { t } = useTranslation();
  const v4 = useFlags().v4_enabled;
  const { toggle, isFav } = useFavorites();
  const [sp] = useSearchParams();
  const [acts, setActs] = useState([]);
  const [sel, setSel] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [sort, setSort] = useState("");
  const [category, setCategory] = useState("");
  const [island, setIsland] = useState(sp.get("country") || "");
  const [onlyFav, setOnlyFav] = useState(sp.get("fav") === "1");

  useEffect(() => {
    api.get("/activities", { params: { country: island, sort, category: onlyFav ? "" : category } })
      .then((r) => setActs(r.data.activities)).catch(() => {});
  }, [island, sort, category, onlyFav]);

  useEffect(() => { setOnlyFav(sp.get("fav") === "1"); }, [sp]);

  useEffect(() => {
    if (sel) api.get(`/activities/${sel.id}/reviews`).then((r) => setReviews(r.data.reviews)).catch(() => {});
    else setReviews([]);
  }, [sel]);

  return (
    <div className="section-sand min-h-[80vh] py-12 px-4" data-testid="activities-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-heading font-extrabold text-4xl text-ink">{t("activities.title")}</h1>
        <div className="flex items-center justify-between gap-3 mt-2 mb-5 flex-wrap">
          <p className="text-muted2 font-body">{t("activities.sub")}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select data-testid="activities-island" value={island} onChange={(e) => setIsland(e.target.value)} className="h-10 rounded-full bg-white border border-border px-4 text-sm font-semibold text-ink">
              <option value="">{t("activities.allIslands")}</option>
              {ALL_ISLANDS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
            </select>
            <select data-testid="activities-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-10 rounded-full bg-white border border-border px-4 text-sm font-semibold text-ink">
              <option value="">{t("activities.sortRecent")}</option>
              <option value="rating">{t("activities.sortRating")}</option>
              <option value="price_asc">{t("activities.sortPriceAsc")}</option>
              <option value="price_desc">{t("activities.sortPriceDesc")}</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          <button data-testid="activities-cat-all" onClick={() => setCategory("")} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${category === "" ? "bg-canard text-white" : "bg-white border border-border text-ink hover:bg-pastel/40"}`}>{t("activities.catAll")}</button>
          {CATS.map((c) => (
            <button key={c} data-testid={`activities-cat-${c}`} onClick={() => setCategory(c)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${category === c ? "bg-canard text-white" : "bg-white border border-border text-ink hover:bg-pastel/40"}`}>{t(`partnerReq.${CAT_LABEL[c]}`)}</button>
          ))}
          <button data-testid="activities-only-fav" onClick={() => setOnlyFav((v) => !v)} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${onlyFav ? "bg-red-500 text-white" : "bg-white border border-border text-ink hover:bg-pastel/40"}`}><Heart className={`w-4 h-4 ${onlyFav ? "fill-white" : ""}`} /> {t("activities.favorites")}</button>
        </div>
        {acts.length === 0 ? (
          <p data-testid="activities-empty" className="text-muted2 font-body">{t("activities.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(onlyFav ? acts.filter((a) => isFav(a.id)) : acts).map((a) => (
              <div key={a.id} data-testid={`activity-${a.id}`} className="relative bg-white rounded-2xl border border-border overflow-hidden shadow-card">
                <button data-testid={`activity-fav-${a.id}`} onClick={() => toggle(a.id)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 shadow-sm flex items-center justify-center hover:scale-110 transition-transform">
                  <Heart className={`w-4 h-4 ${isFav(a.id) ? "fill-red-500 text-red-500" : "text-muted2"}`} />
                </button>
                <Link to={`/activites/${a.id}`} className="relative block">
                  <img src={fileUrl(a.image)} alt={a.name} className="w-full h-44 object-cover" />
                  <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                    {a.bookings_count >= 3 && <span data-testid={`activity-popular-${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-canard px-2.5 py-1 rounded-full shadow-sm">🔥 {t("activities.popular")}</span>}
                    {a.partner_id && <span data-testid={`activity-verified-${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-emeraude-dark bg-white/90 px-2.5 py-1 rounded-full shadow-sm"><ShieldCheck className="w-3.5 h-3.5" /> {t("activities.verified")}</span>}
                  </div>
                </Link>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/activites/${a.id}`} className="font-heading font-bold text-ink hover:text-canard transition-colors">{a.name}</Link>
                    {a.rating > 0 && <span className="flex items-center gap-1 text-sm font-bold text-canard-dark shrink-0"><Star className="w-4 h-4 fill-emeraude text-emeraude" /> {a.rating}</span>}
                  </div>
                  <p className="text-sm text-muted2 mt-1 line-clamp-2">{a.description}</p>
                  {a.bookings_count > 0 && <p data-testid={`activity-social-${a.id}`} className="text-xs font-semibold text-emeraude-dark mt-2">🔥 {t("activities.alreadyBooked", { n: a.bookings_count })}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm font-bold text-canard">{t("activities.from")} {a.price_from}€</span>
                    <Button data-testid={`activity-request-${a.id}`} onClick={() => setSel(a)} className="rounded-full bg-canard hover:bg-canard-dark h-9 text-sm font-bold">{v4 ? t("activities.book") : t("activities.request")}</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ActivityBookingDialog activity={sel} open={!!sel} onOpenChange={(o) => !o && setSel(null)} v4={v4} reviews={reviews} onSuccess={() => setSel(null)} testidPrefix="act" />
    </div>
  );
}
